import React, { useState, useEffect } from "react";
import { useBusiness } from "../contexts/BusinessContext";
import { supabase } from "../lib/supabase";

export default function BusinessSettingsForm() {
  const { selectedBusiness, updateBusiness } = useBusiness();
  if (!selectedBusiness) return null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [originalFormData, setOriginalFormData] = useState({
    name: selectedBusiness?.name || "",
    type: selectedBusiness?.type || "",
    email: selectedBusiness?.email || "",
    email_verified: selectedBusiness?.email_verified || false,
    phone: selectedBusiness?.phone || "",
    address: selectedBusiness?.address || "",
    timezone: selectedBusiness?.timezone || "America/Vancouver",
    currency: selectedBusiness?.currency || "CAD",
    gst_number: selectedBusiness?.gst_number || "",
    pst_number: selectedBusiness?.pst_number || "",
    industry: selectedBusiness?.industry || "Automotive",
  });
  const [formData, setFormData] = useState({...originalFormData});

  
  // Track if form has changes
  const hasChanges = React.useMemo(() => {
    return (
      formData.name !== originalFormData.name ||
      formData.email !== originalFormData.email ||
      formData.phone !== originalFormData.phone ||
      formData.address !== originalFormData.address ||
      formData.timezone !== originalFormData.timezone ||
      formData.currency !== originalFormData.currency ||
      formData.gst_number !== originalFormData.gst_number ||
      formData.pst_number !== originalFormData.pst_number ||
      formData.industry !== originalFormData.industry
    );
  }, [formData, originalFormData]);

  // Initialize form data on component mount or business change
  useEffect(() => {
    if (selectedBusiness) {
      const newData = {
        name: selectedBusiness.name || "",
        type: selectedBusiness.type || "",
        email: selectedBusiness.email || "",
        email_verified: selectedBusiness.email_verified || false,
        phone: selectedBusiness.phone || "",
        address: selectedBusiness.address || "",
        timezone: selectedBusiness.timezone || "America/Vancouver",
        currency: selectedBusiness.currency || "CAD",
        gst_number: selectedBusiness.gst_number || "",
        pst_number: selectedBusiness.pst_number || "",
        industry: selectedBusiness?.industry || "Automotive",
      };
      setOriginalFormData(newData);
      setFormData(newData);
    }
  }, [selectedBusiness]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Skip submission if no actual changes
    if (!hasChanges) {
      setLoading(false);
      return;
    }

    try {
      if (!supabase) {
        throw new Error("Supabase client not initialized");
      }

      const { error } = await supabase
        .from("businesses")
        .update({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          timezone: formData.timezone,
          currency: formData.currency,
          gst_number: formData.gst_number,
          pst_number: formData.pst_number,
          industry: formData.industry,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedBusiness.id);

      if (error) throw error;

      setSuccess("Business settings updated successfully");
      await updateBusiness(selectedBusiness.id, formData);
      // Update original form data to match current data after successful save
      setOriginalFormData({...formData});
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update business settings"
      );
    } finally {
      setLoading(false);
    }
  };
  
  const resetForm = () => {
    setFormData({...originalFormData});
    setError(null);
    setSuccess(null);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">
            Business Name
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            className="w-full h-9 px-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-all"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">
            Business Address
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, address: e.target.value }))
            }
            className="w-full h-9 px-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-all"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, phone: e.target.value }))
              }
              className="w-full h-9 px-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              Time Zone
            </label>
            <select
              value={formData.timezone}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, timezone: e.target.value }))
              }
              className="w-full h-9 px-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-all"
            >
              <option value="America/Vancouver">Pacific Time (British Columbia)</option>
              <option value="America/Whitehorse">Yukon Time</option>
              <option value="America/Edmonton">Mountain Time (Alberta)</option>
              <option value="America/Dawson_Creek">Mountain Time – no DST (Northeast BC)</option>
              <option value="America/Regina">Central Time – no DST (Saskatchewan)</option>
              <option value="America/Winnipeg">Central Time (Manitoba)</option>
              <option value="America/Toronto">Eastern Time (Ontario / Quebec)</option>
              <option value="America/Halifax">Atlantic Time (NS / NB / PEI)</option>
              <option value="America/St_Johns">Newfoundland Time</option>
              {/* Keep legacy US values so existing businesses still display correctly */}
              <option value="America/Denver">Mountain Time (US)</option>
              <option value="America/Chicago">Central Time (US)</option>
              <option value="America/New_York">Eastern Time (US)</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              Contact Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              className="w-full h-9 px-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              Currency
            </label>
            <select
              value={formData.currency}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, currency: e.target.value }))
              }
              className="w-full h-9 px-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-all"
            >
              <option value="CAD">CAD ($)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">
            PST Number
          </label>
          <input
            type="text"
            value={formData.pst_number}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, pst_number: e.target.value }))
            }
            className="w-full h-9 px-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-all"
            placeholder="Optional"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">
            GST Number
          </label>
          <input
            type="text"
            value={formData.gst_number}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, gst_number: e.target.value }))
            }
            className="w-full h-9 px-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-all"
            placeholder="Optional"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">
            Industry
          </label>
          <select
            value={formData.industry}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, industry: e.target.value }))
            }
            className="w-full h-9 px-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-all"
          >
            <option value="Automotive">Automotive</option>
            <option value="Home Services">Home Services</option>
          </select>
        </div>
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-3 py-2 rounded-lg text-sm">
          {success}
        </div>
      )}

      {hasChanges && (
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200/60">
          <button
            type="button"
            onClick={resetForm}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      )}
    </form>
  );
}
